import { Card } from "react-bootstrap";
import CustomTableSortable from "../../components/CustomTable/CustomTableSortable";
import { useEffect, useRef, useState } from "react"; // ✅ added useRef
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";
import { EyeFill } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import { TrashFill } from "react-bootstrap-icons";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import ShareService from "../../utility/ShareService"; // ✅ added

// ✅ added
const PAGE_FILTER_KEY = "membership_requests_page_filter";

type StatusType = "PENDING" | "RESOLVED" | "DELETED";

interface PlanInfo {
  _id: string;
  planName: string;
  amount: number;
  frequency: "MONTH" | "YEAR";
}

interface UserInfo {
  _id: string;
  name?: string;
  email?: string;
}

interface MembershipRequestRow {
  _id: string;
  userId?: UserInfo;
  planId?: PlanInfo;
  requestId?: string;
  OrginationName?: string;
  companyLastYearRevenue?: number;
  status: StatusType;
  createdAt: string;
  resolvedComment?: string;
  primaryContactFullName?: string;
  primaryContactEmail?: string;
}

interface MetaInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  sortField: string;
  sortOrder: "asc" | "desc";
}

const MembershipRequestListPage = () => {
  // ✅ restore saved filter
  const savedFilter = ShareService.getMapValueAndRemove(PAGE_FILTER_KEY);

  const [membershipRequests, setMembershipRequests] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilter?.currentPage ?? 1); // ✅
  const itemsPerPage = 10;

  const [meta, setMeta] = useState<MetaInfo | null>(null);

  const [statusFilter, setStatusFilter] = useState<string>(savedFilter?.statusFilter ?? "ALL"); // ✅
  const [fromDate, setFromDate] = useState<string>(savedFilter?.fromDate ?? ""); // ✅
  const [toDate, setToDate] = useState<string>(savedFilter?.toDate ?? ""); // ✅

  const [sortField, setSortField] = useState<string>(savedFilter?.sortField ?? "createdAt"); // ✅
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(savedFilter?.sortOrder ?? "desc"); // ✅
  const [uiSortField, setUiSortField] = useState<string>(savedFilter?.uiSortField ?? "createdAtFormatted"); // ✅

  const [searchInput, setSearchInput] = useState<string>(savedFilter?.searchInput ?? ""); // ✅
  const searchRef = useRef<string>(savedFilter?.searchInput ?? ""); // ✅
  const [keyword, setKeyword] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<string | null>(null);

  const [resolveModalOpen, setResolveModalOpen] = useState<boolean>(false);
  const [resolveComment, setResolveComment] = useState<string>("");
  const [selectedRequest, setSelectedRequest] = useState<MembershipRequestRow | null>(null);
  const [resolving, setResolving] = useState<boolean>(false);
  const [pagePermission, setPagePermission] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });
  const navigate = useNavigate();

  const handleDeleteRequest = (id: string) => {
    setRequestToDelete(id);
    setShowDeleteModal(true);
  };

  const formatDate = (iso: string | undefined) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleString();
  };

  const formatAmount = (v?: number) => {
    if (v === undefined || v === null) return "-";
    return `₹${(v / 100).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const badgeClass = (status: StatusType) => {
    switch (status) {
      case "PENDING":
        return "badge bg-warning text-dark";
      case "RESOLVED":
        return "badge bg-success";
      case "DELETED":
        return "badge bg-danger";
      default:
        return "badge bg-secondary";
    }
  };

  const sortFieldMap: Record<string, string> = {
    createdAtFormatted: "createdAt",
    organization: "OrginationName",
    requestId: "requestId",
    user: "userId.email",
    primaryContactName: "primaryContactFullName",
    primaryContactEmail: "primaryContactEmail",
    lastYearRevenue: "companyLastYearRevenue",
    statusBadge: "status",
    plan: "planId.planName",
  };

  // ✅ save filter before navigating
  const saveCurrentFilter = () => {
    ShareService.setMapValue(PAGE_FILTER_KEY, {
      searchInput: searchRef.current,
      statusFilter,
      fromDate,
      toDate,
      currentPage,
      sortField,
      sortOrder,
      uiSortField,
    });
  };

  // ✅ saveCurrentFilter before navigate
  const handleViewDetails = (id: string) => {
    saveCurrentFilter();
    navigate(`/membership-requests/${id}`);
  };

  const fetchData = (keyword = "") => {
    setLoading(true);

    const params = new URLSearchParams();
    params.append("page", currentPage.toString());
    params.append("limit", itemsPerPage.toString());

    if (statusFilter !== "ALL") {
      params.append("status", statusFilter);
    }
    if (fromDate) {
      params.append("fromDate", fromDate);
    }
    if (toDate) {
      params.append("toDate", toDate);
    }
    if (keyword.trim()) {
      params.append("keyword", keyword.trim());
    }
    if (sortField) {
      params.append("sortField", sortField);
    }
    if (sortOrder) {
      params.append("sortOrder", sortOrder);
    }

    WebService.getAPI({
      action: `plans/membership-requests/list?${params.toString()}`,
      isShowError: true,
    })
      .then((res: any) => {
        const perm = res?.permissions || {};

        setPagePermission({
          canView: perm.canView,
          canAdd: perm.canAdd,
          canEdit: perm.canEdit,
          canDelete: perm.canDelete,
        });

        const list: MembershipRequestRow[] = res?.data || [];
        setMeta(res?.meta || null);
        setTotalCount(res?.meta?.total || 0);

        const formattedRows = list.map((item: MembershipRequestRow) => ({
          ...item,
          requestId: item.requestId,
          createdAtFormatted: formatDate(item.createdAt),
          plan: item.planId ? (
            <div>
              <div className="fw-semibold">{item.planId.planName}</div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                {formatAmount(item.planId.amount)} /{" "}
                {item.planId.frequency === "YEAR" ? "year" : "month"}
              </div>
            </div>
          ) : (
            "-"
          ),
          organization: item.OrginationName || "-",
          // lastYearRevenue:
          //   item.companyLastYearRevenue !== undefined &&
          //   item.companyLastYearRevenue !== null
          //     ? item.companyLastYearRevenue.toLocaleString("en-IN")
          //     : "-",
          user: item.userId?.email !== undefined && item.userId?.email !== null ? item.userId?.email : "-",
          primaryContactName: item.primaryContactFullName !== undefined && item.primaryContactFullName !== null ? item.primaryContactFullName : "-",
          primaryContactEmail: item.primaryContactEmail !== undefined && item.primaryContactEmail !== null ? item.primaryContactEmail : "-",
          statusBadge: (
            <span className={badgeClass(item.status)}>
              {item.status}
            </span>
          ),
          action: (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ gap: 8 }}
            >
              {perm.canEdit && (
                <EyeFill
                  size={22}
                  role="button"
                  className="text-primary"
                  title="View Details"
                  onClick={() => handleViewDetails(item._id)}
                />
              )}

              {perm.canEdit && (
                <div style={{ width: 80, textAlign: "center" }}>
                  {item.status !== "RESOLVED" ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => openResolveModal(item)}
                    >
                      Resolve
                    </button>
                  ) : (
                    <span style={{ visibility: "hidden" }}>Resolve</span>
                  )}
                </div>
              )}

              {perm.canDelete && (
                <TrashFill
                  size={18}
                  color="#DC143C"
                  className="cursor-pointer"
                  title="Delete"
                  onClick={() => handleDeleteRequest(item._id)}
                />
              )}
            </div>
          ),
        }));

        setMembershipRequests(formattedRows);
      })
      .catch((err: any) => {
        console.error("Error loading membership requests", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // ✅ pass searchRef.current on mount so restored search is used
  useEffect(() => {
    fetchData(searchRef.current);
  }, [currentPage, statusFilter, fromDate, toDate, sortField, sortOrder, keyword]);

  const onSearchClick = () => {
    setCurrentPage(1);
    setKeyword(searchInput);
  };

  const openResolveModal = (row: MembershipRequestRow) => {
    setSelectedRequest(row);
    setResolveComment("");
    setResolveModalOpen(true);
  };

  const closeResolveModal = () => {
    setResolveModalOpen(false);
    setSelectedRequest(null);
    setResolveComment("");
  };

  const handleResolveSubmit = () => {
    if (!selectedRequest?._id) return;

    if (!resolveComment.trim()) {
      toast.error("Please enter a comment before resolving.");
      return;
    }

    setResolving(true);

    WebService.postAPI({
      action: `plans/membership-requests/resolve`,
      body: {
        id: selectedRequest._id,
        resolvedComment: resolveComment,
      },
      isShowError: true,
    })
      .then((res: any) => {
        toast.success(res?.message || "Request resolved successfully.");
        closeResolveModal();
        fetchData(searchRef.current); // ✅
      })
      .catch((err: any) => {
        console.error("Error resolving request", err);
      })
      .finally(() => {
        setResolving(false);
      });
  };

  const handleExportExcel = async () => {
    setLoading(true);

    try {
      const params = new URLSearchParams();

      if (statusFilter !== "ALL") params.append("status", statusFilter);
      if (fromDate) params.append("fromDate", fromDate);
      if (toDate) params.append("toDate", toDate);

      if (searchInput.trim()) params.append("keyword", searchInput.trim());

      if (sortField) params.append("sortField", sortField);
      if (sortOrder) params.append("sortOrder", sortOrder);

      const res: any = await WebService.getBlobAPI({
        action: `plans/membership-requests/export?${params.toString()}`,
        responseType: "blob",
      });

      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "membership-requests-export.csv";
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("Export failed", err);

      if (err?.response?.data instanceof Blob) {
        const text = await err.response.data.text();

        try {
          const json = JSON.parse(text);
          toast.error(json.message || "No data found");
        } catch {
          toast.error("No membership requests found for selected filters");
        }
      } else {
        toast.error(err?.response?.data?.message || "Export failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={() => {
          if (requestToDelete) {
            WebService.deleteAPI({
              action: `plans/membership-requests/delete/${requestToDelete}`,
            })
              .then(() => {
                toast.success("Membership request deleted successfully");
                setShowDeleteModal(false);
                setRequestToDelete(null);
                fetchData(searchRef.current); // ✅
              })
              .catch(() => {
                toast.error("Failed to delete membership request");
              });
          }
        }}
        message="Are you sure you want to delete this membership request?"
      />
      <div className="events-main">
        <Card className="event-card">
          <div className="mb-3">
            <div className="page-title mb-3">Membership Requests</div>

            <div className="d-flex justify-content-between w-100">

              <div className="d-flex gap-3">

                <div style={{ width: "300px" }}>
                  <label className="form-label  mb-1" style={{ fontSize: 12 }}>
                    Search (Org / Email / Primary Contact / Primary Email)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type keyword"
                    value={searchInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      searchRef.current = value; // ✅
                      setSearchInput(value);
                      setCurrentPage(1);
                      fetchData(value);
                    }}
                  />
                </div>

                <div style={{ width: "200px" }}>
                  <label className="form-label  mb-1" style={{ fontSize: 12 }}>
                    Status
                  </label>
                  <select
                    className="form-control"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="ALL">All</option>
                    <option value="PENDING">Pending</option>
                    <option value="RESOLVED">Resolved</option>
                  </select>
                </div>

              </div>

              {pagePermission.canView && (
                <div className="d-flex align-items-end mb-2">
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: 120, height: 38 }}
                    onClick={handleExportExcel}
                  >
                    Export
                  </button>
                </div>
              )}

            </div>
          </div>

          <CustomTableSortable
            columns={[
              {
                id: "requestId",
                label: "Request Id",
                // minWidth: 170,
                sortable: true,
              },
              {
                id: "createdAtFormatted",
                label: "Date",
                minWidth: 170,
                sortable: true,
              },
              { id: "plan", label: "Plan", minWidth: 170, sortable: true, },
              {
                id: "organization",
                label: "Organization",
                minWidth: 170,
                sortable: true,
              },
              { id: "user", label: "User", minWidth: 170, sortable: true, },
              {
                id: "primaryContactName",
                label: "Primary Contact",
                minWidth: 150,
                sortable: true,
              },
              {
                id: "primaryContactEmail",
                label: "Primary Email",
                minWidth: 150,
                sortable: true,
              },
              {
                id: "statusBadge",
                label: "Status",
                minWidth: 100,
                sortable: true,
              },
              {
                id: "action",
                label: "Action",
                minWidth: 140,
                align: "center",
              },
            ]}
            rows={membershipRequests}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={itemsPerPage}
            onPageChange={(page) => {
              setCurrentPage(page);
            }}
            isLoading={loading}
            sortField={uiSortField}
            sortOrder={sortOrder}
            onSortChange={(columnId, order) => {
              setUiSortField(columnId);
              setSortOrder(order);
              const apiField = sortFieldMap[columnId] || "createdAt";
              setSortField(apiField);
              setCurrentPage(1);
            }}
          />
        </Card>
      </div>

      {resolveModalOpen && selectedRequest && (
        <div
          className="modal show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Resolve Membership Request</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeResolveModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to mark{" "}
                  <strong>
                    {selectedRequest.OrginationName || "this request"}
                  </strong>{" "}
                  as <strong>RESOLVED</strong>?
                </p>
                <div className="mb-2">
                  <label className="form-label">Comment (required)</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Add resolution notes / comments"
                    value={resolveComment}
                    onChange={(e) => setResolveComment(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeResolveModal}
                  disabled={resolving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleResolveSubmit}
                  disabled={resolving}
                >
                  {resolving ? "Resolving..." : "Confirm Resolve"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MembershipRequestListPage;
